/**
 * vibecheck scan — Core scanning command
 * 
 * Wires into the real 17-engine scanner from src/scanner/index.ts:
 *   credentials, security, fake-features, hallucinations, dead-ui,
 *   code-quality, import-graph, runtime-verify, ast-analysis, flow-trace,
 *   string-claim-miner, import-reality, fake-success-ui, mock-detector,
 *   runtime-probe, phantom-deps, placeholder-scanner
 */

import * as fs from 'fs';
import * as path from 'path';
import type { Command } from 'commander';
import ora from 'ora';
import type { GlobalOptions, SeverityLevel, ScanResult } from '../types';
import { loadConfig, ensureVibecheckDir } from '../core/config';
import { walkChangedFiles, walkStagedFiles } from '../core/file-walker';
import * as out from '../ui/output';
import { colors, icons, severityIcon } from '../ui/theme';

// ── Import the real 17-engine scanner ───────────────────────────────────────

let realScanner: typeof import('../../../engines') | null = null;

async function getScanner() {
  if (!realScanner) {
    try {
      realScanner = await import('../../../engines');
    } catch {
      // Fallback: scanner module not built yet
      realScanner = null;
    }
  }
  return realScanner;
}

// ── Engine presets for focused scans ────────────────────────────────────────

const ENGINE_PRESETS: Record<string, string[]> = {
  secrets: ['credentials', 'placeholder-scanner'],
  mock: ['mock-detector', 'fake-features', 'fake-success-ui', 'placeholder-scanner'],
  todos: ['placeholder-scanner', 'code-quality'],
  security: ['credentials', 'security'],
  hallucinations: ['hallucinations', 'import-reality', 'phantom-deps', 'string-claim-miner'],
  quality: ['code-quality', 'fake-features', 'dead-ui'],
  all: [], // empty = all engines
};

export function registerScan(program: Command): void {
  const cmd = program
    .command('scan')
    .description('Scan codebase for issues (hallucinations, security, quality)')
    .option('--severity <level>', 'Minimum severity: critical, high, medium, low', 'low')
    .option('--format <type>', 'Output format: pretty, json, markdown, sarif', 'pretty')
    .option('--output <path>', 'Write report to file')
    .option('--fix', 'Auto-fix simple issues where possible')
    .option('--ignore <patterns>', 'Comma-separated glob patterns to ignore')
    .option('--ci', 'CI-friendly output (no color, no spinners)')
    .option('--max-issues <n>', 'Stop after N issues found', parseInt as any)
    .option('--engines <list>', 'Comma-separated engine names to run')
    .option('--include-tests', 'Include test files in scan');

  // ── Subcommands ───────────────────────────────────────────────────────────

  cmd.command('full').description('Complete scan — all 17 engines, full project').action(async (_o: any, c: Command) => {
    await runScan({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any), mode: 'full' });
  });

  cmd.command('quick').description('Fast scan — changed files only (git diff)').action(async (_o: any, c: Command) => {
    await runScan({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any), mode: 'quick' });
  });

  cmd.command('file <path>').description('Scan a single file').action(async (filePath: string, _o: any, c: Command) => {
    await runScan({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any), mode: 'file', target: filePath });
  });

  cmd.command('dir <path>').description('Scan a specific directory').action(async (dirPath: string, _o: any, c: Command) => {
    await runScan({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any), mode: 'dir', target: dirPath });
  });

  cmd.command('staged').description('Scan only git staged files (pre-commit hook)').action(async (_o: any, c: Command) => {
    await runScan({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any), mode: 'staged' });
  });

  // ── Focused scan subcommands ──────────────────────────────────────────────

  cmd.command('secrets').description('Scan for hardcoded secrets, API keys, tokens, passwords (credentials + placeholder engines)').action(async (_o: any, c: Command) => {
    await runScan({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any), mode: 'full', preset: 'secrets' });
  });

  cmd.command('mock').description('Scan for mock data leaking into production (mock-detector + fake-features + fake-success-ui)').action(async (_o: any, c: Command) => {
    await runScan({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any), mode: 'full', preset: 'mock' });
  });

  cmd.command('todos').description('Scan for TODO/FIXME markers, AI skeleton stubs, placeholder code (placeholder-scanner + code-quality)').action(async (_o: any, c: Command) => {
    await runScan({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any), mode: 'full', preset: 'todos' });
  });

  cmd.command('security').description('Scan for injection, XSS, SSRF, prototype pollution (security + credentials engines)').action(async (_o: any, c: Command) => {
    await runScan({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any), mode: 'full', preset: 'security' });
  });

  cmd.command('hallucinations').description('Scan for AI-hallucinated code: ghost imports, phantom deps, fake packages').action(async (_o: any, c: Command) => {
    await runScan({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any), mode: 'full', preset: 'hallucinations' });
  });

  // Default action
  cmd.action(async (opts: any, command: Command) => {
    await runScan({ ...(command.parent!.opts() as GlobalOptions), ...opts, mode: 'full' });
  });
}

// ── Types ───────────────────────────────────────────────────────────────────

interface ScanOpts extends GlobalOptions {
  mode: 'full' | 'quick' | 'file' | 'dir' | 'staged';
  target?: string;
  severity?: string;
  format?: string;
  output?: string;
  fix?: boolean;
  ignore?: string;
  ci?: boolean;
  maxIssues?: number;
  engines?: string;
  preset?: string;
  includeTests?: boolean;
}

// ── Main Scan Runner ────────────────────────────────────────────────────────

async function runScan(opts: ScanOpts): Promise<void> {
  const start = performance.now();
  const rootPath = opts.path || process.cwd();
  const isSilent = opts.quiet || opts.ci;

  // Resolve engine list
  let engineList: string[] | undefined;
  if (opts.preset && ENGINE_PRESETS[opts.preset]) {
    engineList = ENGINE_PRESETS[opts.preset];
    if (engineList.length === 0) engineList = undefined; // all engines
  } else if (opts.engines) {
    engineList = opts.engines.split(',').map(e => e.trim());
  }

  const presetLabel = opts.preset ? ` (${opts.preset})` : '';

  // Try real scanner first
  const scanner = await getScanner();

  if (scanner) {
    return runWithRealScanner(scanner, opts, rootPath, engineList, presetLabel, start, !!isSilent);
  }

  // Fallback to v2 lite scanner
  return runWithLiteScanner(opts, rootPath, presetLabel, start, !!isSilent);
}

// ── Real 17-Engine Scanner ──────────────────────────────────────────────────

async function runWithRealScanner(
  scanner: NonNullable<typeof realScanner>,
  opts: ScanOpts,
  rootPath: string,
  engineList: string[] | undefined,
  presetLabel: string,
  start: number,
  isSilent: boolean,
): Promise<void> {
  const spinner = ora({ text: `Scanning${presetLabel}...`, isSilent }).start();

  // Build scan options for the real scanner
  const scanOptions: any = {
    projectRoot: rootPath,
    severityThreshold: opts.severity || 'low',
    includeTests: opts.includeTests || false,
    parallel: true,
    engines: engineList,
    onProgress: (progress: any) => {
      if (progress.phase === 'loading') spinner.text = 'Loading files...';
      else if (progress.phase === 'classifying') spinner.text = `Classified ${progress.total} files...`;
      else if (progress.phase === 'deduplicating') spinner.text = `Deduplicating ${progress.total} findings...`;
      else if (progress.phase === 'complete') spinner.text = 'Complete';
    },
  };

  // Handle file/dir/staged/quick modes
  if (opts.mode === 'file' && opts.target) {
    scanOptions.files = [opts.target];
  } else if (opts.mode === 'dir' && opts.target) {
    // The real scanner walks from projectRoot, so we set projectRoot to the dir
    scanOptions.projectRoot = path.resolve(rootPath, opts.target);
  } else if (opts.mode === 'staged') {
    const staged = await walkStagedFiles(rootPath);
    if (staged.length === 0) { spinner.warn('No staged files found.'); return; }
    scanOptions.files = staged;
  } else if (opts.mode === 'quick') {
    const changed = await walkChangedFiles(rootPath);
    if (changed.length === 0) { spinner.warn('No changed files found.'); return; }
    scanOptions.files = changed;
  }

  // Ignore patterns
  if (opts.ignore) {
    scanOptions.exclude = opts.ignore.split(',').map((p: string) => p.trim());
  }

  // Run the real scanner
  let report: any;
  try {
    report = await scanner.scan(scanOptions);
  } catch (err: any) {
    spinner.fail('Scan failed');
    out.error(err.message || 'Unknown scanner error');
    process.exitCode = 3;
    return;
  }

  spinner.stop();
  const duration = performance.now() - start;

  // Extract findings
  const findings = report.findings || [];
  const summary = report.summary || {};
  const healthScore = report.healthScore ?? 100;
  const engineResults = report.engineResults || [];

  // JSON output
  if (opts.json || opts.format === 'json') {
    out.jsonOutput({
      ...report,
      metrics: { ...report.metrics, durationMs: Math.round(duration) },
    });
    process.exitCode = (summary.bySeverity?.critical ?? 0) > 0 ? 2 : (summary.bySeverity?.high ?? 0) > 0 ? 1 : 0;
    return;
  }

  // Write to file if requested
  if (opts.output) {
    const outputPath = path.resolve(rootPath, opts.output);
    fs.writeFileSync(outputPath, JSON.stringify(report, null, 2), 'utf-8');
    out.info(`Report written to ${outputPath}`);
  }

  // Pretty output
  renderPrettyReport(findings, summary, healthScore, engineResults, duration, opts, presetLabel);
}

// ── Lite Scanner Fallback ───────────────────────────────────────────────────

async function runWithLiteScanner(
  opts: ScanOpts,
  rootPath: string,
  presetLabel: string,
  start: number,
  isSilent: boolean,
): Promise<void> {
  // Import the v2 lite scanner as fallback
  const { scanFile, resetScanner } = await import('../core/scanner');
  const { walkFiles, walkChangedFiles: walkChanged, walkStagedFiles: walkStaged, walkSingleFile, walkDir } = await import('../core/file-walker');
  const config = loadConfig(rootPath);
  resetScanner();

  const spinner = ora({ text: `Scanning${presetLabel} (lite mode)...`, isSilent }).start();

  let files: string[];
  switch (opts.mode) {
    case 'quick': files = await walkChanged(rootPath); break;
    case 'staged': files = await walkStaged(rootPath); break;
    case 'file': { const f = await walkSingleFile(rootPath, opts.target!); files = f ? [f] : []; break; }
    case 'dir': files = await walkDir(rootPath, opts.target!); break;
    default: files = await walkFiles(rootPath, { include: config.scan.include, exclude: config.scan.exclude });
  }

  if (files.length === 0) { spinner.warn('No files found to scan.'); return; }
  spinner.text = `Scanning ${files.length} files${presetLabel}...`;

  const allFindings: any[] = [];
  for (const file of files) {
    const findings = scanFile(rootPath, file);
    allFindings.push(...findings);
    if (opts.maxIssues && allFindings.length >= opts.maxIssues) break;
  }

  spinner.stop();
  const duration = performance.now() - start;

  const bySeverity = {
    critical: allFindings.filter(f => f.severity === 'critical').length,
    high: allFindings.filter(f => f.severity === 'high').length,
    medium: allFindings.filter(f => f.severity === 'medium').length,
    low: allFindings.filter(f => f.severity === 'low').length,
    info: allFindings.filter(f => f.severity === 'info').length,
  };

  const healthScore = Math.max(0, 100 - (bySeverity.critical * 15 + bySeverity.high * 8 + bySeverity.medium * 3 + bySeverity.low));

  // Build ScanResult for formatters
  const scanResult: ScanResult = {
    success: bySeverity.critical === 0,
    findings: allFindings,
    stats: {
      filesScanned: files.length,
      totalFindings: allFindings.length,
      ...bySeverity,
      duration,
    },
    timestamp: new Date().toISOString(),
  };

  // Save scan results to .vibecheck/
  try {
    const vcDir = ensureVibecheckDir(rootPath);
    fs.writeFileSync(path.join(vcDir, 'scan-results.json'), JSON.stringify(scanResult, null, 2), 'utf-8');
  } catch { /* ignore write errors */ }

  // Format-specific output
  if (opts.json || opts.format === 'json') {
    out.jsonOutput({ ...scanResult, healthScore });
    process.exitCode = bySeverity.critical > 0 ? 2 : bySeverity.high > 0 ? 1 : 0;
    return;
  }

  if (opts.format === 'sarif') {
    const sarif = out.toSarif(scanResult);
    if (opts.output) {
      fs.writeFileSync(path.resolve(rootPath, opts.output), JSON.stringify(sarif, null, 2), 'utf-8');
      out.info(`SARIF report written to ${opts.output}`);
    } else {
      console.log(JSON.stringify(sarif, null, 2));
    }
    process.exitCode = bySeverity.critical > 0 ? 2 : bySeverity.high > 0 ? 1 : 0;
    return;
  }

  if (opts.format === 'markdown') {
    const md = out.toMarkdown(scanResult);
    if (opts.output) {
      fs.writeFileSync(path.resolve(rootPath, opts.output), md, 'utf-8');
      out.info(`Markdown report written to ${opts.output}`);
    } else {
      console.log(md);
    }
    process.exitCode = bySeverity.critical > 0 ? 2 : bySeverity.high > 0 ? 1 : 0;
    return;
  }

  if (opts.format === 'html') {
    const html = out.toHtml(scanResult);
    const outputPath = opts.output || path.join(ensureVibecheckDir(rootPath), 'scan-report.html');
    fs.writeFileSync(path.resolve(rootPath, outputPath), html, 'utf-8');
    out.info(`HTML report written to ${outputPath}`);
    process.exitCode = bySeverity.critical > 0 ? 2 : bySeverity.high > 0 ? 1 : 0;
    return;
  }

  renderPrettyReport(allFindings, { totalFiles: files.length, totalFindings: allFindings.length, bySeverity, byCategory: {}, byEngine: {} }, healthScore, [], duration, opts, presetLabel + ' lite');
}

// ── Pretty Report Renderer ──────────────────────────────────────────────────

function renderPrettyReport(
  findings: any[],
  summary: any,
  healthScore: number,
  engineResults: any[],
  duration: number,
  opts: ScanOpts,
  presetLabel: string,
): void {
  const bySev = summary.bySeverity || {};
  const totalFiles = summary.totalFiles || summary.filesScanned || 0;
  const totalFindings = summary.totalFindings || findings.length;

  out.brandHeader(`Scan Results${presetLabel}`);
  out.nl();

  // Engine summary (always show if engines ran)
  if (engineResults.length > 0) {
    console.log(colors.bold('  Engines:'));
    for (const er of engineResults) {
      const status = er.success ? colors.success('✓') : colors.error('✗');
      const count = er.findings > 0 ? colors.warning(` (${er.findings})`) : colors.dim(' (0)');
      const time = opts.verbose ? colors.dim(` ${er.durationMs}ms`) : '';
      console.log(`    ${status} ${er.engine}${count}${time}`);
    }
    out.nl();
  }

  // Stats line
  const enginesRan = engineResults.filter(e => e.success).length;
  const enginesLabel = enginesRan > 0 ? ` · ${enginesRan} engines` : '';
  console.log(colors.dim(`  ${totalFiles} files scanned${enginesLabel} · ${out.timing(duration)}`));
  out.nl();

  // Health score bar
  const barWidth = 40;
  const filledWidth = Math.round((healthScore / 100) * barWidth);
  const emptyWidth = barWidth - filledWidth;
  const barColor = healthScore >= 80 ? colors.success : healthScore >= 50 ? colors.warning : colors.error;
  console.log(`  Health  ${barColor('█'.repeat(filledWidth))}${colors.dim('░'.repeat(emptyWidth))}  ${colors.bold(String(healthScore))}${colors.dim('/100')}`);
  out.nl();

  if (totalFindings === 0) {
    out.success('All checks passed — your code is clean and ready to ship');
    out.nl();
    process.exitCode = 0;
    return;
  }

  // Summary counts
  const parts: string[] = [];
  if (bySev.critical > 0) parts.push(colors.critical(`${bySev.critical} critical`));
  if (bySev.high > 0) parts.push(colors.high(`${bySev.high} high`));
  if (bySev.medium > 0) parts.push(colors.medium(`${bySev.medium} medium`));
  if (bySev.low > 0) parts.push(colors.low(`${bySev.low} low`));
  console.log(`  ${parts.join(colors.dim(' · '))}`);
  out.nl();

  // Category breakdown
  const byCategory = summary.byCategory || {};
  if (Object.keys(byCategory).length > 0) {
    console.log(colors.bold('  By category:'));
    for (const [cat, count] of Object.entries(byCategory).sort((a, b) => (b[1] as number) - (a[1] as number))) {
      const catIcon = cat.includes('hallucin') ? icons.brain : cat.includes('secur') || cat.includes('cred') ? icons.shield : cat.includes('mock') || cat.includes('fake') ? icons.fire : cat.includes('quality') ? icons.gear : icons.magnify;
      console.log(`    ${catIcon} ${cat}: ${colors.bold(String(count))}`);
    }
    out.nl();
  }

  // Grouped findings display (by file, sorted by severity)
  if (opts.verbose) {
    out.renderGroupedFindings(findings);
  } else {
    // Top findings (grouped by severity) — compact view
    const severityOrder = ['critical', 'high', 'medium', 'low'];
    for (const sev of severityOrder) {
      const group = findings.filter((f: any) => f.severity === sev);
      if (group.length === 0) continue;

      console.log(`${severityIcon(sev)} ${colors.bold(sev.toUpperCase())} (${group.length})`);

      const showAll = sev === 'critical';
      const toShow = showAll ? group : group.slice(0, 5);

      for (const finding of toShow) {
        const engine = finding.engine ? colors.dim(`[${finding.engine}]`) : finding.ruleId ? colors.dim(`[${finding.ruleId}]`) : '';
        const conf = finding.confidenceScore ? colors.dim(` ${finding.confidenceScore}%`) : '';
        console.log(`  ${colors.dim(finding.file)}:${colors.dim(String(finding.line))} ${engine}${conf}`);
        console.log(`  ${finding.message}`);
        if (finding.fix) {
          console.log(`  ${colors.info(icons.arrow)} ${colors.dim(finding.fix)}`);
        }
        console.log();
      }

      if (!showAll && group.length > 5) {
        console.log(`  ${colors.dim(`... and ${group.length - 5} more (use --verbose to see all)`)}`);
        console.log();
      }
    }
  }

  // Category breakdown
  if (findings.length > 0) {
    out.renderCategoryBreakdown(findings);
  }

  // Engine stats
  out.renderEngineStats(totalFiles, totalFindings, duration);

  // Footer
  console.log(out.divider());
  const autoFixable = summary.autoFixable || findings.filter((f: any) => f.autoFixable).length;
  if (autoFixable > 0) {
    console.log(`${autoFixable} auto-fixable. Run ${colors.info('vibecheck scan full --fix')} to apply.`);
  }

  if ((bySev.critical || 0) > 0 || (bySev.high || 0) > 0) {
    console.log(colors.bold('Fix critical and high issues before deploying.'));
  }

  out.nl();
  console.log(out.completedIn(duration));
  process.exitCode = (bySev.critical || 0) > 0 ? 2 : (bySev.high || 0) > 0 ? 1 : 0;
}
